import React from 'react'
import { v4 as uuidv4 } from 'uuid';

import Checkbox from '../../../../Components/Checkbox/Checkbox'
import { CalculateCount } from '../../../../helper/CalculateCount';
import "./ResultTable.css"
import "../../../../Components/Table.css"

const ROUNDS = [1, 2, 3];

// Detect if results use the new slot-mode shape: scores[i].slot_index is set.
const isSlotModeResults = (results) =>
    Array.isArray(results) &&
    results.length > 0 &&
    Array.isArray(results[0]?.scores) &&
    results[0].scores.some(s => s.slot_index != null);

// Row at index (countOfTopPlaces - 1) gets `increasedBorderBottom` — the placer
// separator. Same logic used by OneJudgeResult / ThreeJudgeResult so the cutoff
// is consistent across all result views (driven by event.percentageToAward and
// event.minimumNumberToAward — see CalculateCount).
const rowClassName = (index, countOfTopPlaces) => {
    if (countOfTopPlaces && index === countOfTopPlaces - 1) return "increasedBorderBottom";
    return "";
};

// A dancer counts as having competed only if at least one judge gave them a
// real (non-zero) score. Filters out DNS / withdrawn dancers who stay in
// competition.dancers[] but never danced — without this they rendered as
// zero-filled trailing rows in Complete Results (e.g. C19 OPEN CHAMP in WBD
// 2026 showed two phantom rows with all zeros).
const hasAnyScore = (dancer) =>
    Array.isArray(dancer?.scores) &&
    dancer.scores.some(s => s && s.score != null && Number(s.score) > 0);

const NineJudgeResult = ({ results, keyProp, event }) => {
    const scoredResults = (results || []).filter(hasAnyScore);
    const slotMode = isSlotModeResults(scoredResults);
    const countOfTopPlaces = event && scoredResults.length > 0 ? CalculateCount(event, scoredResults) : 0;

    if (slotMode) {
        return renderSlotMode(scoredResults, keyProp, countOfTopPlaces);
    }
    return renderLegacy(scoredResults, keyProp, countOfTopPlaces);
};

// New layout: 3 rounds × 3 slots each, each cell bound to a stable slot_index.
// Header carries judge name per slot.
const renderSlotMode = (results, keyProp, countOfTopPlaces) => {
    // Pull a representative slot layout from the first dancer (all dancers share the same slots).
    const firstDancer = results[0] || {};
    const slotsByRound = ROUNDS.map(r =>
        (firstDancer.scores || [])
            .filter(s => s.round === r)
            .sort((a, b) => (a.position_in_round || 0) - (b.position_in_round || 0))
    );

    const slotSubtotal = (dancer, round) =>
        (dancer.scores || [])
            .filter(s => s.round === round)
            .reduce((acc, s) => acc + (s.irish_points || 0), 0);

    return (
        <div style={{ width: "100%", overflowX: "scroll", marginTop: "30px", marginBottom: "50px" }} key={keyProp}>
            <table className="table my_table competitorTable">
                <tbody>
                    <tr className="header-row">
                        <td className="text-center p-25-5 w-3">Award Received</td>
                        <td className="text-center p-25-5 w-2">#</td>
                        <td className="text-center w-3">Final Place</td>
                        <td className="text-center w-4">Total Irish Points</td>
                        <td className="mx-5 p-25-5 w-9">Competitor</td>
                        {ROUNDS.map(r => (
                            <td key={`r-${r}`} className="text-center grey-42 p-25-5 w-9">{`Round ${r}`}</td>
                        ))}
                    </tr>
                    <tr style={{ background: "#EAEAEA", fontSize: "14px", height: "31px" }}>
                        <td className="text-center grey-42 p-25-5 w-3"></td>
                        <td className="text-center grey-42 p-25-5 w-2"></td>
                        <td className="text-center w-3"></td>
                        <td className="text-center w-4"></td>
                        <td className="mx-5 grey-42 p-25-5 w-9"></td>
                        {ROUNDS.map((r, idx) => (
                            <td key={`hdr-${r}`} className="text-center grey-42 p-25-5 w-3">
                                <div className="d-flex gap-1 px-2">
                                    {slotsByRound[idx].slice(0, 3).map((slot, sIdx) => {
                                        const judgeName =
                                            (slot.judge_fname || slot.judge_lname)
                                                ? `${slot.judge_fname || ""} ${slot.judge_lname || ""}`.trim()
                                                : (slot.judge_details?.name || "-");
                                        return (
                                            <div key={`hdr-${r}-${slot.slot_index}`} className="nineJudgeHead col-3 align-items-center">
                                                {judgeName}
                                            </div>
                                        );
                                    })}
                                    <div className="nineJudgeHead col-3 align-items-center">
                                        Sub Total<br />R{r}
                                    </div>
                                </div>
                            </td>
                        ))}
                    </tr>
                    {results.map((dancer, dIdx) => (
                        <tr key={dancer.dancer_id || uuidv4()} className={rowClassName(dIdx, countOfTopPlaces)} style={{ background: "#FFFFFF", fontSize: "12px", height: "31px" }}>
                            <td className="text-center mx-5 py-4 w-3 grey-30"><Checkbox /></td>
                            <td className="text-center mx-5 py-4 w-2 grey-30">{dancer.card_number?.card_number ?? dancer.card_number ?? ""}</td>
                            <td className="text-center mx-5 py-4 w-3 grey-30">{dancer.overallPosition}</td>
                            <td className="text-center mx-5 py-4 w-4 grey-30">{dancer.irish_points}</td>
                            <td className="mx-5 w-9 grey-30">
                                <div>{dancer.dancer_fname} &nbsp;{dancer.dancer_lname}<div style={{ opacity: "0.3" }}>{dancer.school}</div></div>
                            </td>
                            {ROUNDS.map((r, idx) => {
                                const slots = slotsByRound[idx];
                                const dancerSlotsThisRound = slots.map(layoutSlot =>
                                    (dancer.scores || []).find(s => s.slot_index === layoutSlot.slot_index)
                                );
                                const sub = slotSubtotal(dancer, r);
                                return (
                                    <td key={`d-${dancer.dancer_id}-r-${r}`} className="text-center grey-30 p-25-5 w-3">
                                        <div className="d-flex align-items-center gap-1 px-2">
                                            {dancerSlotsThisRound.slice(0, 3).map((s, sIdx) => (
                                                <div key={`d-${dancer.dancer_id}-s-${idx}-${sIdx}`} className='col-3'>
                                                    {s ? s.score : "-"}
                                                </div>
                                            ))}
                                            <div className='col-3'>{Number(sub.toFixed(2))}</div>
                                        </div>
                                    </td>
                                );
                            })}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
};

// Legacy implementation — unchanged layout. Renders the original round-bucketed view for old completed events.
const renderLegacy = (results, keyProp, countOfTopPlaces) => {
    const refinedResults = results.map(dancer => {
        const refinedScores = [[], [], []]
        dancer?.scores?.forEach((item) => {
            refinedScores[item?.scores_by_round[0]?.round - 1].push({ judge: item?.judge_details?.name, score: item?.scores_by_round[0]?.score, irish_points: item.irish_points });
        });
        return {
            cardNumber: dancer?.card_number?.card_number,
            fname: dancer?.dancer_fname,
            lname: dancer?.dancer_lname,
            school: dancer?.school,
            overallPosition: dancer?.overallPosition,
            overallScore: dancer?.irish_points,
            scores: refinedScores
        }
    })
    let roundCount = [1, 2, 3]
    return (
        <div style={{ width: "100%", overflowX: "scroll", marginTop: "30px", marginBottom: "50px" }} key={keyProp}>
            <table className="table my_table competitorTable">
                <tbody>
                    <tr className="header-row">
                        <td className="text-center p-25-5 w-3">Award Received</td>
                        <td className="text-center p-25-5 w-2">#</td>
                        <td className="text-center w-3">Final Place</td>
                        <td className="text-center w-4">Total Irish Points</td>
                        <td className="mx-5 p-25-5 w-9">Competitor</td>
                        {roundCount.map((item) => {
                            return <td key={uuidv4()} className="text-center grey-42 p-25-5 w-9">{`Round ${item}`}</td>
                        })}
                    </tr>
                    <tr style={{ background: "#EAEAEA", fontSize: "14px", height: "31px" }}>
                        <td className="text-center grey-42 p-25-5 w-3"></td>
                        <td className="text-center grey-42 p-25-5 w-2"></td>
                        <td className="text-center w-3"></td>
                        <td className="text-center w-4"></td>
                        <td className="mx-5 grey-42 p-25-5 w-9"></td>
                        {refinedResults[0]?.scores?.map((item, index) => {
                            return <td key={uuidv4()} className="text-center grey-42 p-25-5 w-3">
                                <div className="d-flex  gap-1 px-2">
                                    <div className="nineJudgeHead col-3 align-items-center">{item[0] ? item[0]?.judge : "-"}</div>
                                    <div className="nineJudgeHead col-3 align-items-center">{item[1] ? item[1]?.judge : "-"}</div>
                                    <div className="nineJudgeHead col-3 align-items-center">{item[2] ? item[2]?.judge : "-"}</div>
                                    <div className="nineJudgeHead col-3 align-items-center"> Total Irish Points<br />{index === 0 ? "1st" : index === 1 ? "2nd" : "3rd"} Round </div>
                                </div>
                            </td>
                        })}
                    </tr>
                    {refinedResults?.map((item, index) => {
                        return <tr key={uuidv4()} className={rowClassName(index, countOfTopPlaces)} style={{ background: "#FFFFFF", fontSize: "12px", height: "31px" }}>
                            <td className="text-center mx-5 py-4 w-3 grey-30"><Checkbox /></td>
                            <td className="text-center mx-5 py-4 w-2 grey-30">{item?.cardNumber}</td>
                            <td className="text-center mx-5 py-4 w-3 grey-30">{item?.overallPosition}</td>
                            <td className="text-center mx-5 py-4 w-4 grey-30">{item?.overallScore}</td>
                            <td className="mx-5 w-9 grey-30">
                                <div>{item?.fname} &nbsp;{item?.lname}<div style={{ opacity: "0.3" }}>{item?.school}</div></div>
                            </td>

                            {refinedResults[index]?.scores?.map((item, index) => {
                                return <td key={uuidv4()} className="text-center grey-30 p-25-5 w-3">
                                    <div className="d-flex align-items-center gap-1 px-2">
                                        <div className='col-3'>{item[0] ? item[0]?.score : "-"}</div>
                                        <div className='col-3'>{item[1] ? item[1]?.score : "-"}</div>
                                        <div className='col-3'>{item[2] ? item[2]?.score : "-"}</div>
                                        <div className='col-3'>{(item[0]?.irish_points ? item[0]?.irish_points : 0) + (item[1]?.irish_points ? item[1]?.irish_points : 0) + (item[2]?.irish_points ? item[2]?.irish_points : 0)}</div>

                                    </div>
                                </td>
                            })}
                        </tr>
                    })}

                </tbody>
            </table>
        </div >
    )
}

export default NineJudgeResult
